import type {
  AgenticSkill,
  BackendSkill,
  BackendSkillRequest,
  ChatState,
  FleetAgentMessage,
  FleetAgentSession,
  SkillApprovalStatus,
  SkillChatMessage,
  SkillChatSession,
  SkillDepartment,
  SkillTaskMetadata,
  SkillTaskStatus,
  SkillUser,
} from "./types";

export const currentUser = {
  id: "agruning",
  email: "agruning@polygon.technology",
  department: "Engineering",
  team: "wallet",
};

export function currentUserMatches(id?: string | null, email?: string | null): boolean {
  return (
    Boolean(id && id.toLowerCase() === currentUser.id.toLowerCase()) ||
    Boolean(email && email.toLowerCase() === currentUser.email.toLowerCase())
  );
}

export function normalizeStatus(value?: string): SkillApprovalStatus {
  switch ((value ?? "").toLowerCase()) {
    case "approved":
    case "available":
      return "approved";
    case "declined":
    case "rejected":
      return "declined";
    default:
      return "pending";
  }
}

export function formatStatus(status: SkillApprovalStatus): string {
  if (status === "approved") return "Approved";
  if (status === "declined") return "Declined";
  return "In review";
}

export function formatTaskStatus(status: SkillTaskStatus): string {
  switch (status.kind) {
    case "waiting":
      return "Waiting";
    case "running":
      return "Running";
    case "in_review":
      return "In review";
    case "approved":
      return "Approved";
    case "declined":
      return "Declined";
  }
}

export function normalizeBackendSkill(skill: BackendSkill): AgenticSkill {
  const id = skill.installId || skill.id || slug(skill.title || skill.name || "skill");
  const name = skill.title || skill.name || id;
  const creator = skill.creator ?? undefined;

  return {
    id,
    name,
    summary: skill.description || "No description provided.",
    details: skill.details || skill.description || "No description provided.",
    isRelevant: Boolean(skill.relevant),
    isCreatedByUser: currentUserMatches(creator?.id, creator?.email),
    status: normalizeStatus(skill.status),
    creatorID: creator?.id,
    creatorName: creator?.name,
    creatorEmail: creator?.email,
    creatorDepartment: creator?.department,
    creatorTeam: creator?.team ?? undefined,
    lastUpdated: formatDate(skill.updatedAt) || "Unknown",
    pullRequestURL: skill.pullRequest?.url ?? undefined,
  };
}

export function buildUsers(skills: AgenticSkill[]): SkillUser[] {
  const creators = new Map<string, SkillUser>();

  for (const skill of skills) {
    const id = skill.creatorID || (skill.isCreatedByUser ? currentUser.id : "unknown");
    const existing = creators.get(id);
    const nextSkillList = [...(existing?.skills ?? []), skill].sort((a, b) => a.name.localeCompare(b.name));
    creators.set(id, {
      id,
      name: skill.creatorName || (skill.isCreatedByUser ? "Andy Gruning" : "Unknown"),
      email: skill.creatorEmail || (skill.isCreatedByUser ? currentUser.email : "Unknown"),
      department: skill.creatorDepartment || (skill.isCreatedByUser ? currentUser.department : "Unknown"),
      team: skill.creatorTeam || (skill.isCreatedByUser ? currentUser.team : undefined),
      skills: nextSkillList,
    });
  }

  if (!creators.has(currentUser.id)) {
    creators.set(currentUser.id, {
      id: currentUser.id,
      name: "Andy Gruning",
      email: currentUser.email,
      department: currentUser.department,
      team: currentUser.team,
      skills: skills.filter((skill) => skill.isCreatedByUser),
    });
  }

  return [...creators.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function buildDepartments(skills: AgenticSkill[]): SkillDepartment[] {
  if (skills.length === 0) return [];

  const groups = new Map<string, AgenticSkill[]>();
  for (const skill of skills) {
    const department = skill.creatorDepartment || (skill.isCreatedByUser ? currentUser.department : "Marketplace");
    groups.set(department, [...(groups.get(department) ?? []), skill]);
  }

  return [...groups.entries()]
    .map(([department, departmentSkills]) => ({
      id: slug(department),
      name: department,
      plugins: [
        {
          id: `${slug(department)}-skills`,
          name: `${slug(department)}-skills`,
          title: "Available skills",
          description: "Approved and pending marketplace skills.",
          skills: departmentSkills.sort((a, b) => a.name.localeCompare(b.name)),
        },
      ],
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function normalizeSession(session: FleetAgentSession, titleOverride?: string): SkillChatSession {
  const id = stringValue(session.id ?? session.sessionId ?? session.session_id ?? session.runId) || crypto.randomUUID();
  const title = titleOverride || stringValue(session.title ?? session.name ?? session.summary) || `Session ${id.slice(0, 8)}`;
  const messages = (session.messages ?? session.events ?? []).map(normalizeMessage).filter(Boolean) as SkillChatMessage[];
  const updatedAt = stringValue(session.updatedAt ?? session.updated_at ?? session.createdAt ?? session.created_at) || undefined;

  return { id, title: updatedAt && !title.includes(updatedAt.slice(0, 10)) ? `${title} - ${updatedAt.slice(0, 16).replace("T", " ")}` : title, messages, updatedAt };
}

export function normalizeTaskStatus(request?: BackendSkillRequest, fleetStatus?: string): SkillTaskStatus {
  const status = (request?.status ?? fleetStatus ?? "").toLowerCase();
  const pullRequestURL = request?.pullRequest?.url ?? undefined;

  if (status === "waiting for review" || status === "in review") {
    return { kind: "in_review", pullRequestURL };
  }
  if (status === "available" || status === "approved") {
    return { kind: "approved", pullRequestURL };
  }
  if (status === "declined" || status === "rejected") {
    return { kind: "declined", pullRequestURL };
  }
  if (status === "waiting") {
    return { kind: "waiting", pullRequestURL };
  }
  return { kind: "running", pullRequestURL };
}

export function taskMetadata(request?: BackendSkillRequest): SkillTaskMetadata {
  return {
    skillID: request?.skill?.id ?? request?.skillID ?? undefined,
    name: request?.skill?.title ?? request?.skill?.name ?? undefined,
    description: request?.skill?.description ?? undefined,
    pullRequestBody: request?.pullRequest?.body ?? undefined,
  };
}

export function sampleSkills(): AgenticSkill[] {
  return [
    {
      id: "design-architect",
      name: "Design Architect",
      summary: "Generate design-system UI components and apply shared themes across app platforms.",
      details:
        "Generate design-system UI components, build design-system-styled apps, and retrofit existing app screens for platform parity.",
      isRelevant: true,
      isCreatedByUser: true,
      status: "approved",
      creatorID: currentUser.id,
      creatorName: "Andy Gruning",
      creatorEmail: currentUser.email,
      creatorDepartment: currentUser.department,
      creatorTeam: currentUser.team,
      lastUpdated: "July 1, 2026",
    },
    {
      id: "agent-doctor",
      name: "Agent Doctor",
      summary: "Audit a repository for agent readiness and remediate missing project guidance.",
      details:
        "Audits repositories for agent readiness, then prepares guidance files, testing notes, and workflow improvements for coding agents.",
      isRelevant: true,
      isCreatedByUser: false,
      status: "approved",
      creatorID: "ai-foundations-team",
      creatorName: "AI Foundations Team",
      creatorDepartment: "AI Foundations Team",
      lastUpdated: "July 1, 2026",
    },
    {
      id: "move-jira-to-github",
      name: "Move Jira to GitHub",
      summary: "Move SPEC Jira tickets into the GitHub repository and project board.",
      details:
        "Migrates a Polygon SPEC Jira ticket to GitHub, adds it to the project board, and closes the original ticket with a link back.",
      isRelevant: false,
      isCreatedByUser: false,
      status: "approved",
      creatorID: "platform-team",
      creatorName: "Platform Team",
      creatorDepartment: "Platform Team",
      lastUpdated: "July 1, 2026",
    },
  ];
}

function normalizeMessage(message: FleetAgentMessage): SkillChatMessage | null {
  const eventType = stringValue(message.type).toLowerCase();
  if (eventType.includes(".") && eventType !== "user.message" && eventType !== "agent.message" && eventType !== "assistant.message") {
    return null;
  }

  const text = stringValue(message.text ?? contentText(message.content) ?? message.message ?? message.output ?? message.output_text);
  if (!text) return null;

  const role = stringValue(message.role ?? message.sender ?? message.type).toLowerCase();
  return {
    id: stringValue(message.id) || crypto.randomUUID(),
    sender: role.includes("user") ? "user" : "assistant",
    text,
  };
}

function contentText(content: FleetAgentMessage["content"]): string {
  if (Array.isArray(content)) {
    return content.map((block) => block.text).filter(Boolean).join("\n\n");
  }
  return stringValue(content);
}

function stringValue(value: unknown): string {
  if (value === undefined || value === null) return "";
  return String(value);
}

function formatDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en", { month: "long", day: "numeric", year: "numeric" }).format(date);
}

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
